import * as filestack from 'filestack-js'
import type { Client, PickerFileMetadata, PickerOptions } from 'filestack-js'

export type { PickerFileMetadata, PickerOptions }

let client: Client | undefined

const getClient = (): Client => {
  if (!client) {
    client = filestack.init(window.FILE_STACK_KEY)
  }

  return client
}

/**
 * Raised when the picker is dismissed without a successful upload, so that callers can
 * distinguish "user backed out" from a genuine upload failure.
 */
export class PickerDismissedError extends Error {
  constructor() {
    super('The file picker closed before a file finished uploading')
    this.name = 'PickerDismissedError'
  }
}

/**
 * Raised when a file never finishes transferring to our storage bucket.
 */
export class FileNotStoredError extends Error {
  constructor(handle: string) {
    super(`Filestack handle ${handle} was still in transit after ${STORE_POLL_ATTEMPTS} attempts`)
    this.name = 'FileNotStoredError'
  }
}

const STORE_POLL_ATTEMPTS = 10
const STORE_POLL_INITIAL_DELAY_MS = 300
const STORE_POLL_BACKOFF = 1.5
const STORE_POLL_MAX_DELAY_MS = 2000

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const isStored = (file: PickerFileMetadata) => Boolean(file.container && file.key)

/**
 * `onUploadDone` fires once a file reaches Filestack's edge, which is *before* it has been
 * persisted to our bucket. Until then its status is `InTransit` and `container`/`key` are
 * absent. Filestack exposes no callback for the final transfer, so poll the file's metadata
 * until the store lands
 */
const waitForStoredFile = async (file: PickerFileMetadata): Promise<PickerFileMetadata> => {
  if (isStored(file)) {
    return file
  }

  let wait = STORE_POLL_INITIAL_DELAY_MS

  for (let attempt = 0; attempt < STORE_POLL_ATTEMPTS; attempt += 1) {
    await delay(wait)
    wait = Math.min(wait * STORE_POLL_BACKOFF, STORE_POLL_MAX_DELAY_MS)

    try {
      const metadata = await getClient().metadata(file.handle, {
        container: true,
        path: true,
        location: true,
      })

      // The metadata endpoint calls the stored object's path what the picker calls its key.
      const key = metadata?.key ?? metadata?.path

      if (metadata?.container && key) {
        return { ...file, container: metadata.container, key, status: 'Stored' }
      }
    } catch {
      // A transient metadata failure just means we try again on the next tick.
    }
  }

  throw new FileNotStoredError(file.handle)
}

/**
 * Opens the Filestack picker and resolves once every picked file has finished transferring
 * to our storage bucket, so `container` and `key` are always populated.
 * Rejects if the picker is dismissed without a successful upload, or if a file never stores.
 */
export const pickAndStore = (
  pickerOptions: Omit<PickerOptions, 'onUploadDone' | 'onClose'>,
): Promise<PickerFileMetadata[]> =>
  new Promise((resolve, reject) => {
    let settled = false

    getClient()
      .picker({
        ...pickerOptions,
        onUploadDone: ({ filesUploaded }) => {
          if (!filesUploaded.length) {
            return
          }

          settled = true
          Promise.all(filesUploaded.map(waitForStoredFile)).then(resolve, reject)
        },
        onClose: () => {
          // `onUploadDone` fires before the picker tears itself down, so defer a tick and
          // let a successful upload settle first rather than racing it.
          setTimeout(() => {
            if (settled) {
              return
            }

            settled = true
            reject(new PickerDismissedError())
          })
        },
      })
      .open()
  })

export default { pickAndStore }
